/**
 * Reserved subdomain labels that must never be resolved as tenant slugs.
 *
 * Single source of truth — imported by:
 *   - src/middleware.ts (authoritative for hostname → slug parsing at the edge)
 *   - src/lib/tenant-context.tsx (defense-in-depth on the client for
 *     stale cookies / older middleware deploys)
 *
 * Before this module existed, the two consumers maintained their own copies
 * and could drift (e.g. `resumes` / `dashboard` were in middleware but not
 * the client list — caught by the architecture audit). Don't re-introduce
 * a parallel constant.
 *
 * Edge-Runtime safe — no Node/server imports, pure data.
 */

const _LITERAL_LIST = [
  'api',
  'resumes',
  'www',
  'admin',
  'dashboard',
  'tenant',
  'app',
  'assets',
  'static',
  'cdn',
] as const;

export type ReservedSubdomain = (typeof _LITERAL_LIST)[number];

// Exported as `readonly string[]` (not the literal union) so callers can
// pass arbitrary strings to .includes() without TypeScript narrowing the
// argument. The literal type is still available via `ReservedSubdomain`
// for places that want it.
export const RESERVED_SUBDOMAINS: readonly string[] = _LITERAL_LIST;

/**
 * Pure helper — true when the given slug should NOT be resolved to a tenant.
 * Trims + lowercases for safety.
 *
 * Custom-domain slugs (prefixed with `custom:`) are never reserved — they're
 * actual hostnames like `careers.bootminds.com`, not subdomain labels.
 */
export function isReservedSubdomain(slug: string | null | undefined): boolean {
  if (!slug) return false;
  if (slug.startsWith('custom:')) return false;
  return (RESERVED_SUBDOMAINS as readonly string[]).includes(
    slug.trim().toLowerCase(),
  );
}
